import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { cn } from "@/lib/utils";
import {
  FileText, Search, ClipboardList, BarChart3, FileBarChart,
  Bell, Landmark, ShieldCheck, LogOut, Menu, X, Building2, Search as SearchIcon, HelpCircle, Eye, Newspaper, Info,
  Inbox, Lightbulb, CalendarDays, FolderArchive, ExternalLink, ChevronDown,
} from "lucide-react";
import { NotificationBell } from "@/components/layout/NotificationBell";

// Primary areas (max 5). Ordinary staff see My Work / Insights / Meetings.
const PRIMARY = [
  { to: "/my-work", label: "My Work", icon: Inbox, min: "viewer" },
  { to: "/insights", label: "Insights", icon: Lightbulb, min: "viewer" },
  { to: "/meetings", label: "Meetings", icon: CalendarDays, min: "viewer" },
  { to: "/records", label: "Records", icon: FolderArchive, cap: true },
  { to: "/admin", label: "Administration", icon: ShieldCheck, min: "manager" },
];

// Secondary legislative-intelligence tools (collapsed by default; routes preserved).
const MORE = [
  { to: "/brief", label: "Executive Brief", icon: Newspaper, min: "viewer" },
  { to: "/bills", label: "Bills", icon: FileText, min: "viewer" },
  { to: "/search", label: "Search", icon: Search, min: "viewer" },
  { to: "/assignments", label: "Assignments", icon: ClipboardList, min: "viewer" },
  { to: "/watchlists", label: "Watchlists", icon: Eye, min: "viewer" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, min: "viewer" },
  { to: "/reports", label: "Reports", icon: FileBarChart, min: "analyst" },
  { to: "/alerts", label: "Alerts", icon: Bell, min: "viewer" },
  { to: "/hub", label: "Gov Info Hub", icon: Landmark, min: "viewer" },
];

const ALL_TITLES = [...PRIMARY, ...MORE, { to: "/guide", label: "Help & Guide" }, { to: "/dashboard", label: "My Work" }];
const ROLE_LABEL = { administrator: "Administrator", manager: "Manager", analyst: "Policy Analyst", viewer: "Viewer" };
const testId = (label) => `nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

export default function AppShell() {
  const { user, logout } = useAuth();
  const config = usePublicConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState(null);
  const [q, setQ] = useState("");
  const [isBoard, setIsBoard] = useState(false);
  const caps = user?.capabilities || [];
  const canRecords = caps.includes("records_management") || caps.includes("data_export");
  const canSee = (n) => (n.cap ? canRecords : hasMinRole(user?.role, n.min));
  const primary = PRIMARY.filter(canSee);
  const more = MORE.filter(canSee);
  const inMore = more.some((n) => location.pathname === n.to || location.pathname.startsWith(n.to + "/"));
  const [moreOpen, setMoreOpen] = useState(inMore);

  useEffect(() => { api.get("/organization").then((r) => setOrg(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/forum/my-memberships").then((r) => setIsBoard(!!r.data?.is_board_user)).catch(() => {}); }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => { if (inMore) setMoreOpen(true); }, [inMore]);
  useEffect(() => {
    const it = ALL_TITLES.find((n) => location.pathname === n.to || location.pathname.startsWith(n.to + "/"));
    document.title = (it ? `${it.label} | ` : "") + "GovFlowState";
  }, [location.pathname]);

  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`); };

  const NavLink = ({ n }) => {
    const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
    return (
      <Link to={n.to} data-testid={testId(n.label)}
        className={cn("relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
          active ? "bg-white/10 text-white" : "text-white/65 hover:text-white hover:bg-white/[0.06]")}>
        {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-brand-gold" />}
        <n.icon className={cn("h-[18px] w-[18px]", active ? "text-brand-gold" : "text-white/55")} /> {n.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-brand-bg flex">
      <a href="#main-content" className="skip-link" data-testid="skip-to-main">Skip to main content</a>
      {/* Sidebar */}
      <aside className={cn(
        "gf-sidebar fixed z-50 inset-y-0 left-0 w-64 bg-brand-navy flex flex-col transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full")} data-testid="sidebar"
        style={{ backgroundImage: "linear-gradient(180deg, #123B63 0%, #0E3252 100%)" }}>
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-white/10">
          <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm ring-1 ring-white/40 overflow-hidden">
            <img src="/govflow-mark.png" alt="GovFlowState" className="h-7 w-7 object-contain" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-white text-[15px] tracking-tight">GovFlowState</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-brand-civic/90 truncate" title={org?.name || ""}>{org?.name ? `Configured for ${org.name}` : (org?.brand_short || "Legislative")}</div>
          </div>
          <button className="ml-auto lg:hidden text-white/70 hover:text-white" onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" aria-label="Primary">
          {primary.map((n) => <NavLink key={n.to} n={n} />)}

          {more.length > 0 && (
            <div className="pt-3 mt-2 border-t border-white/10">
              <button type="button" onClick={() => setMoreOpen((v) => !v)} data-testid="nav-more-toggle"
                aria-expanded={moreOpen}
                className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/45 hover:text-white/80 transition-colors">
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", moreOpen ? "" : "-rotate-90")} />
                Legislative Intelligence
              </button>
              {moreOpen && <div className="space-y-1 mt-1" data-testid="nav-more-group">{more.map((n) => <NavLink key={n.to} n={n} />)}</div>}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2 bg-white/[0.06]">
            <div className="h-8 w-8 rounded-full bg-brand-civic flex items-center justify-center text-xs font-bold text-white overflow-hidden ring-1 ring-white/20">
              {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "?")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-brand-gold">{ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-button" aria-label="Log out"
              className="text-white/60 hover:text-red-300 transition-colors"><LogOut className="h-[18px] w-[18px]" /></button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-brand-navy-900/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:pl-64 min-w-0">
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-brand-line shadow-card flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-brand-navy" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="open-menu">
            <Menu className="h-6 w-6" />
          </button>
          <form onSubmit={submitSearch} className="flex-1 max-w-md relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
              placeholder="Search bills, sponsors, committees…" aria-label="Global search"
              className="w-full h-9 rounded-lg bg-brand-bg border border-brand-line pl-9 pr-3 text-sm text-brand-ink placeholder:text-slate-400 focus:bg-white focus:border-brand-civic outline-none transition-colors" />
          </form>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {isBoard && (
              <Link to="/forum" data-testid="switch-to-forum"
                className="inline-flex items-center gap-1.5 rounded-lg border border-brand-civic/40 bg-brand-civic-50 px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-brand-civic-50/70">
                <Landmark className="h-3.5 w-3.5" /> Forum View
              </Link>
            )}
            <a href="/public" target="_blank" rel="noopener noreferrer" data-testid="view-public-site"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-brand-line px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-brand-navy hover:border-brand-civic/40 transition-colors"
              title="View Public Site" aria-label="View public transparency site (opens in a new tab)">
              <ExternalLink className="h-3.5 w-3.5" /> Public Site
            </a>
            <Link to="/guide" data-testid="nav-help" title="Help & Guide" aria-label="Help and guide"
              className="text-slate-400 hover:text-brand-navy transition-colors"><HelpCircle className="h-5 w-5" /></Link>
            <NotificationBell />
          </div>
        </header>
        {config?.is_demo && (
          <div data-testid="demo-disclosure" className="flex items-start gap-2 border-b border-brand-gold/30 bg-brand-gold/10 px-4 py-2 text-[12px] leading-snug text-[#7A5B0B] sm:px-6">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span><strong className="font-semibold">Demonstration Environment</strong> — this environment may combine publicly available legislative information with clearly identified sample workflow data for demonstration purposes.</span>
          </div>
        )}
        <main id="main-content" className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto animate-in-up" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
